import { useState, useEffect } from 'react';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { generarRemitoPDFPremium } from '../utils/generadorPdfRemito';
import { registrarProductosVendidos } from '../components/venta/PasoProductosVenta';
import { getTodayISO } from '../utils/dateUtils';

export function useVentaForm(onSaved, clienteInicialId = null, ventaParaEditar = null) {
    const [clientes,  setClientes]  = useState([]);
    const [repuestos, setRepuestos] = useState([]);
    const [clienteId, setClienteId] = useState(clienteInicialId ? String(clienteInicialId) : (ventaParaEditar?.clienteId ? String(ventaParaEditar.clienteId) : null));
    const [productos, setProductos] = useState(() => {
        if (!ventaParaEditar?.items?.length) return [];
        return ventaParaEditar.items.flatMap(it => (it.repuestosUsados || []).map(r => ({
            id: r.id, nombre: r.nombre, sku: r.sku || '',
            precio: parseFloat(r.precio) || 0,
            cantidad: r.cantidad || 1,
            subtotal: (r.cantidad || 1) * (parseFloat(r.precio) || 0),
            fotoUrl: r.fotoUrl || null,
        })));
    });
    const [repuestoElegido, setRepuestoElegido] = useState(null);
    const [descuentoPorcentaje, setDescuentoPorcentaje] = useState(ventaParaEditar?.descuentoPorcentaje || 0);
    const [costoEnvio, setCostoEnvio] = useState('');
    const [fechaVenta, setFechaVenta] = useState(ventaParaEditar?.fecha || getTodayISO());
    const [leyenda, setLeyenda] = useState(ventaParaEditar?.observaciones || '');
    const [modalClienteAbierto, setModalClienteAbierto] = useState(false);
    const [nombreClientePrellenado, setNombreClientePrellenado] = useState('');

    // Estado del flujo de 3 pasos
    const [modoRapido, setModoRapido] = useState(false);
    const [registrarCliente, setRegistrarCliente] = useState(false);
    const [datosCliente, setDatosCliente] = useState({
        nombre: '', telefono: '', email: '',
        calle: '', numero: '', localidad: '', provincia: 'Buenos Aires', notas: ''
    });

    useEffect(() => {
        const cargar = async () => {
            try {
                const [c, r] = await Promise.all([
                    api.get('/clientes?page=0&size=500'),
                    api.get('/repuestos?page=0&size=500')
                ]);
                setClientes(c.data.content || c.data);
                setRepuestos(r.data.content || r.data);
            } catch {
                toast.error('Error de conexión');
            }
        };
        cargar();
    }, []);

    const handleDatosChange = (e) =>
        setDatosCliente(prev => ({ ...prev, [e.target.name]: e.target.value }));

    // Activar modo rápido (MOSTRADOR)
    const activarRapido = (mostradorCid) => {
        if (mostradorCid) { setClienteId(mostradorCid.toString()); setModoRapido(true); }
    };

    // Volver al modo con cliente
    const activarNormal = () => {
        setClienteId(null);
        setModoRapido(false);
        setRegistrarCliente(false);
        setDatosCliente({ nombre: '', telefono: '', email: '', calle: '', numero: '', localidad: '', provincia: 'Buenos Aires', notas: '' });
    };

    const agregarProducto = () => {
        if (!repuestoElegido) return;
        setProductos(prev => {
            const idx = prev.findIndex(p => p.id === repuestoElegido.id);
            if (idx > -1) {
                const nuevos = [...prev];
                nuevos[idx].cantidad += 1;
                nuevos[idx].subtotal  = nuevos[idx].cantidad * nuevos[idx].precio;
                return nuevos;
            }
            return [...prev, {
                id:       repuestoElegido.id,
                nombre:   repuestoElegido.nombre,
                sku:      repuestoElegido.sku,
                precio:   parseFloat(repuestoElegido.precio) || 0,
                cantidad: 1,
                subtotal: parseFloat(repuestoElegido.precio) || 0,
                fotoUrl:  repuestoElegido.fotoUrl || null,
            }];
        });
        setRepuestoElegido(null);
    };

    const actualizarCantidad = (idx, valor) => {
        setProductos(prev => {
            const nuevos = [...prev];
            const qty = Math.max(1, parseInt(valor) || 1);
            nuevos[idx] = { ...nuevos[idx], cantidad: qty, subtotal: qty * nuevos[idx].precio };
            return nuevos;
        });
    };

    const quitarProducto = (idx) => setProductos(prev => prev.filter((_, i) => i !== idx));

    // Estado del modal de creación rápida de repuesto
    const [modalRepuesto, setModalRepuesto]   = useState(false);
    const [nombreRepuesto, setNombreRepuesto] = useState('');

    // Al crear un repuesto desde el modal rápido, añadirlo a la lista y seleccionarlo
    const repuestoCreado = (repuesto) => {
        setRepuestos(prev => [...prev, repuesto]);
        setRepuestoElegido({ ...repuesto, label: `[${repuesto.sku}] ${repuesto.nombre}`, value: repuesto.id });
    };

    // Abrir el modal de creación desde CreatableSelect
    const abrirModalRepuesto = (nombre) => {
        setNombreRepuesto(nombre);
        setModalRepuesto(true);
    };

    const subtotalProductos = productos.reduce((a, b) => a + b.subtotal, 0);
    const envioNum          = parseFloat(costoEnvio) || 0;
    const totalBruto        = subtotalProductos + envioNum;
    const descuentoMonto    = (totalBruto * descuentoPorcentaje) / 100;
    const totalFinal        = totalBruto - descuentoMonto;
    const clienteObj        = clientes.find(c => c.id.toString() === clienteId);

    // Rentabilidad (Lucas, panel "Ver rentabilidad" pedido para Venta, mismo criterio
    // que ya usa Servicio en useServicioForm.js — ver PasoResumen.jsx/RentabilidadPanel).
    // Cada producto de "productos" ya trae costo/porcentajeGanancia desde que se agrega
    // vía RepuestosBottomSheet (armarEntrada) — acá solo se calcula la ganancia con eso.
    const calcularGananciaRepuesto = (repuesto, cantidad) => {
        const precioVenta = parseFloat(repuesto.precio) || 0;
        const costo       = parseFloat(repuesto.costo)  || 0;
        const qty         = parseFloat(cantidad)         || 1;
        const subtotal    = precioVenta * qty;

        if (costo > 0) {
            const costoTotal = costo * qty;
            const ganancia   = subtotal - costoTotal;
            const margen     = subtotal > 0 ? ((ganancia / subtotal) * 100).toFixed(1) : 0;
            return { subtotal, costoTotal, ganancia, margen };
        }

        const pct = parseFloat(repuesto.porcentajeGanancia) || 0;
        if (pct > 0) {
            const costoImplicito = subtotal / (1 + pct / 100);
            const ganancia = subtotal - costoImplicito;
            return { subtotal, costoTotal: costoImplicito, ganancia, margen: pct.toFixed(1) };
        }

        return { subtotal, costoTotal: 0, ganancia: 0, margen: 0 };
    };

    const calcularResumenGanancia = () => {
        let totalVenta = 0;
        let totalCosto = 0;

        productos.forEach(p => {
            const g = calcularGananciaRepuesto(p, p.cantidad);
            totalVenta += g.subtotal;
            totalCosto += g.costoTotal;
        });
        // El envío se suma como venta pura, sin costo asociado — no hay dato de
        // costo de envío en el modelo hoy (mismo criterio que costoExtra/mano de
        // obra en Servicio, que tampoco resta costo).
        totalVenta += envioNum;

        const descuento         = (totalVenta * descuentoPorcentaje) / 100;
        const totalConDescuento = totalVenta - descuento;
        const gananciaBruta     = totalConDescuento - totalCosto;
        const margenFinal       = totalConDescuento > 0
            ? ((gananciaBruta / totalConDescuento) * 100).toFixed(1) : 0;

        return { totalVenta, totalCosto, descuento, totalConDescuento, gananciaBruta, margenFinal };
    };

    const editarCostoProducto = (idx, valorTexto) => {
        const nuevoCosto = parseFloat(valorTexto) || 0;
        setProductos(prev => {
            const copia = [...prev];
            copia[idx] = { ...copia[idx], costo: nuevoCosto };
            return copia;
        });
    };

    // Crea un cliente con datos mínimos antes de guardar la venta
    const crearClienteRapido = async () => {
        try {
            const res = await api.post('/clientes', {
                clienteTipo:  'PARTICULAR',
                nombre:       datosCliente.nombre.trim(),
                telefono:     datosCliente.telefono?.trim() || null,
                email:        datosCliente.email?.trim()    || null,
                condicionIva: 'CONSUMIDOR_FINAL',
                calle:        datosCliente.calle?.trim()    || 'Sin dirección',
                numero:       datosCliente.numero?.trim()   || '0',
                localidad:    datosCliente.localidad?.trim() || 'Sin localidad',
                provincia:    datosCliente.provincia?.trim() || 'Buenos Aires',
                direccion:    datosCliente.calle ? `${datosCliente.calle} ${datosCliente.numero}, ${datosCliente.localidad}` : null,
                notas:        datosCliente.notas?.trim() || null,
            });
            setClientes(prev => [...prev, res.data]);
            return res.data;
        } catch {
            toast.error('Error al registrar cliente');
            return null;
        }
    };

    const resetForm = () => {
        setProductos([]);
        setClienteId(null);
        setDescuentoPorcentaje(0);
        setCostoEnvio('');
        setLeyenda('');
        setModoRapido(false);
        setRegistrarCliente(false);
        setDatosCliente({ nombre: '', telefono: '', email: '', calle: '', numero: '', localidad: '', provincia: 'Buenos Aires', notas: '' });
    };

    // mostradorSid: ID de sede MOSTRADOR para ventas rápidas (de useMostrador)
    const guardarVenta = async (confirmar = false, mostradorSid = null) => {
        if (!clienteId || productos.length === 0) {
            toast.error('Falta cliente o productos');
            return;
        }

        const loading = toast.loading(confirmar ? 'Confirmando...' : 'Guardando...');

        try {
            let nombreFinal = modoRapido
                ? (datosCliente.nombre?.trim() || 'Mostrador')
                : (clienteObj?.nombre || 'Mostrador');

            // Si el usuario quiere registrar el cliente, crearlo antes de guardar
            let clienteReal = !modoRapido ? clienteObj : null;
            if (modoRapido && registrarCliente && datosCliente?.nombre?.trim()) {
                const nuevoCliente = await crearClienteRapido();
                if (nuevoCliente) {
                    nombreFinal = nuevoCliente.nombre;
                    clienteReal = nuevoCliente;
                    toast.success(`Cliente "${nuevoCliente.nombre}" registrado`, { duration: 2000 });
                }
            }

            // Bug 3-oct-2026: TODAS las ventas se guardaban en la sede "Mostrador",
            // aunque fueran de un cliente cargado. Por eso el comprobante salía con
            // "Mostrador" y sin la dirección, teléfono ni CUIT del cliente. Ahora la
            // venta va a la sede del cliente (si no tiene ninguna, se le crea
            // "Principal" con su dirección). Mostrador queda solo para venta rápida.
            let sedeIdFinal = parseInt(mostradorSid || 1);
            let sedeNombreFinal = 'Mostrador';
            if (clienteReal?.id) {
                try {
                    const r = await api.get(`/sedes/cliente/${clienteReal.id}`);
                    let sede = (Array.isArray(r.data) ? r.data : [])[0];
                    if (!sede) {
                        const n = await api.post('/sedes', {
                            clienteId: clienteReal.id, nombreSede: 'Principal',
                            calle: clienteReal.calle, numero: clienteReal.numero, localidad: clienteReal.localidad,
                            provincia: clienteReal.provincia, direccion: clienteReal.direccion,
                        });
                        sede = n.data;
                    }
                    if (sede?.id) { sedeIdFinal = sede.id; sedeNombreFinal = sede.nombreSede || 'Principal'; }
                } catch { /* si falla, queda en Mostrador como antes */ }
            }

            const authUsuario = (() => { try { return JSON.parse(localStorage.getItem('auth_usuario')); } catch { return null; } })();
            const ventaData = {
                sedeId:            sedeIdFinal,
                usuarioId:         authUsuario?.id || 1,
                fecha:             fechaVenta,
                servicioTipo:      'VENTA',
                estado:            confirmar ? 'REALIZADO' : 'PRESUPUESTO',
                clienteNombre:     nombreFinal,
                sedeNombre:        sedeNombreFinal,
                descuentoPorcentaje,
                observaciones:     leyenda || '',
                totalConDescuento: totalFinal,
                items: [{
                    equipoSerial:     'MOSTRADOR',
                    tecnico:          'Mostrador',
                    costo:            totalFinal,
                    costoExtra:       envioNum,
                    metodoPago:       'EFECTIVO',
                    trabajoRealizado: `VENTA: ${productos.map(p => `${p.cantidad}x ${p.nombre}`).join(', ')}${envioNum > 0 ? ` | Envío: $${envioNum}` : ''}`,
                    trabajoTipo:      'VENTA',
                    repuestosUsados:  productos,
                    garantiaHasta:    null
                }]
            };

            // Editar una venta ya guardada actualiza el registro existente en vez de
            // crear un duplicado -- mismo patron que ya usa useServicioForm.js
            // (idEdicion ? PUT : POST). Antes esto siempre hacia POST, sin importar
            // que el modal dijera "Editar Venta #123".
            if (ventaParaEditar?.id) {
                await api.put(`/servicios/${ventaParaEditar.id}`, ventaData);
            } else {
                await api.post('/servicios', ventaData);
            }

            toast.success('¡Venta guardada!', { id: loading });
            registrarProductosVendidos(productos);
            resetForm();
            if (onSaved) onSaved();
        } catch (err) {
            toast.error(`${err.response?.data?.mensaje || err.message}`, { id: loading });
        }
    };

    const dispararPDF = () => {
        const clienteFinal = modoRapido
            ? { nombre: datosCliente.nombre?.trim() || 'Mostrador', telefono: datosCliente.telefono, email: datosCliente.email }
            : (clienteObj || { nombre: 'Mostrador' });
        // Si el cliente tiene una direccion cargada (registrado con Calle/Numero/
        // Localidad, o tipeada en el modo rapido), el PDF la muestra en vez de
        // "Sede: Mostrador" fijo -- antes se ignoraba por completo la direccion
        // real del cliente, aunque estuviera cargada.
        const direccionCliente = !modoRapido
            ? (clienteObj?.direccion || null)
            : (datosCliente.calle ? `${datosCliente.calle} ${datosCliente.numero}, ${datosCliente.localidad}` : null);
        const tecnico = localStorage.getItem('tecnico_nombre') || 'Mostrador';
        generarRemitoPDFPremium({
            tipo:     'PRESUPUESTO_VENTA',
            cliente:  clienteFinal,
            sede:     direccionCliente ? { direccion: direccionCliente } : { nombreSede: 'Mostrador' },
            tecnico,
            ticketItems: [{
                equipoSerial:    'MOSTRADOR',
                repuestosUsados: productos,
                costoExtra:      envioNum,
                totalCalculado:  totalFinal,
            }],
            descuentoPorcentaje,
            leyenda,
            totalFinal,
            fechaServicio: fechaVenta
        });
    };

    const onClienteNuevo = (clienteNuevo) => {
        setClientes(prev => [...prev, clienteNuevo]);
        setClienteId(clienteNuevo.id.toString());
        setModalClienteAbierto(false);
    };

    return {
        clientes, repuestos,
        clienteId, setClienteId,
        clienteObj,
        productos,
        repuestoElegido, setRepuestoElegido,
        descuentoPorcentaje, setDescuentoPorcentaje,
        costoEnvio, setCostoEnvio,
        leyenda, setLeyenda,
        fechaVenta, setFechaVenta,
        descuentoMonto, subtotalProductos, totalBruto, totalFinal, envioNum,
        modalClienteAbierto, setModalClienteAbierto,
        nombreClientePrellenado, setNombreClientePrellenado,
        modoRapido, registrarCliente, setRegistrarCliente,
        datosCliente, handleDatosChange,
        activarRapido, activarNormal,
        agregarProducto, actualizarCantidad, quitarProducto, setProductos,
        modalRepuesto, setModalRepuesto, nombreRepuesto, repuestoCreado, abrirModalRepuesto,
        calcularGananciaRepuesto, calcularResumenGanancia, editarCostoProducto,
        guardarVenta, dispararPDF, onClienteNuevo,
    };
}
